import React, { useCallback, useState, useMemo } from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import { withModal, ModalConsumerProps } from '../modal'
import { ActivityDetail, UserPreference } from 'api'
import { DraggablePanel, DraggableHandle } from 'view/components/panels'
import { ActivityMonitorCloseButton, ActivityMonitorContainer, ActivityMonitorTitle } from './activityMonitorStyles'
import { ActivityMonitorToolbar } from './ActivityMonitorToolbar'
import { ActivityList } from './ActivityList'
import Ticket from 'view/routes/requests/rfqTicket'
import { SelectionActions } from 'store/selection'
import { createAllActivityDetailsSelector } from 'selectors/activityMonitorSelectors'
import { createActivityMonitorOpenSelector } from 'selectors/selectionSelectors'
import { createUserPreferenceSelector } from 'selectors/userPreferenceSelectors'
import { adminClearingAlertSelector } from 'selectors/dealSelectors'
import { Locale, Translation } from 'locale'
import { iconSet } from 'view/components/icons'
import * as constants from 'view/styles/constants'

export interface ActivityMonitorBrowserProps extends ModalConsumerProps {
  allActivityDetails: ActivityDetail[]
  isActivityMonitorOpen: boolean
  showAdminClearingAlert: boolean
  userPreference: UserPreference
  onToggleActivityMonitor: (activityMonitorOpen: boolean) => unknown
}

export const mapStateToProps = createStructuredSelector({
  allActivityDetails: createAllActivityDetailsSelector(),
  isActivityMonitorOpen: createActivityMonitorOpenSelector(),
  showAdminClearingAlert: adminClearingAlertSelector,
  userPreference: createUserPreferenceSelector()
})

export const mapDispatchToProps = {
  onToggleActivityMonitor: SelectionActions.openActivityMonitor
}

export const ActivityMonitorBrowserComponent: React.FunctionComponent<ActivityMonitorBrowserProps> = props => {
  const [sortByTimeLeft, setSortByTimeLeft] = useState(false)
  const { allActivityDetails, isActivityMonitorOpen, showAdminClearingAlert, userPreference, onToggleActivityMonitor, showModal } = props

  const empty = useMemo(() => allActivityDetails.length === 0, [allActivityDetails])

  const handleToggleTimer = useCallback(() => {
    setSortByTimeLeft(prevSortByTimeLeft => !prevSortByTimeLeft)
  }, [])

  const handleActivityClick = useCallback(
    (negotiationId: string, ownOrder: boolean) => {
      showModal(<Ticket negotiationId={negotiationId} ownOrder={ownOrder} />)
    },
    [showModal]
  )

  const renderTitle = useCallback(() => {
    return (
      <DraggableHandle>
        <ActivityMonitorTitle data-test-id={'ActivityMonitorBrowser-ActivityMonitorTitle'}>
          {Locale.translate(Translation.TC_ACTIVITY_MONITOR)}
          <ActivityMonitorCloseButton
            data-test-id={'ActivityMonitorCloseButton'}
            onClick={() => onToggleActivityMonitor(!isActivityMonitorOpen)}>
            {iconSet.crossIcon}
          </ActivityMonitorCloseButton>
        </ActivityMonitorTitle>
      </DraggableHandle>
    )
  }, [isActivityMonitorOpen, onToggleActivityMonitor])

  const renderContainer = useCallback(() => {
    return (
      <ActivityMonitorContainer height="100%">
        {renderTitle()}
        <ActivityMonitorToolbar sortByTimeLeft={sortByTimeLeft} disabled={empty} onToggleTimer={handleToggleTimer} />
        <ActivityList
          allActivityDetails={allActivityDetails}
          showAdminClearingAlert={showAdminClearingAlert}
          userPreference={userPreference}
          sortByTimeLeft={sortByTimeLeft}
          onActivityClick={handleActivityClick}
        />
      </ActivityMonitorContainer>
    )
  }, [
    renderTitle,
    sortByTimeLeft,
    empty,
    handleToggleTimer,
    allActivityDetails,
    showAdminClearingAlert,
    userPreference,
    handleActivityClick
  ])

  if (isActivityMonitorOpen) {
    return (
      <DraggablePanel
        style={{ zIndex: constants.zIndexActivityMonitor, cursor: 'default', pointerEvents: 'all' }}
        dimension={{ x: 100, y: 100, width: 320, height: 450 }}
        minHeight={400}
        resizable={true}>
        {renderContainer()}
      </DraggablePanel>
    )
  } else {
    return null
  }
}

export default withModal(connect(mapStateToProps, mapDispatchToProps)(ActivityMonitorBrowserComponent))
